import classnames from 'classnames';
import ButtonDropdown from 'components/actions/ButtonDropdown';
import NoData from 'components/content/NoData';
import { SystemAdminDialogTypes } from 'helper/types';
import { useCan } from 'hooks/useCan';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    ActiveSidebar,
    getActiveSidebar,
    getActiveUser,
    setActiveSidebar,
    setActiveUser,
    setSystemAdminOpenDialog,
    setUserDialog,
} from 'store/slices/settingsPageSlice';

import DateOrNoData from './DateOrNoData';
import SystemUsersRowStatus from './SystemUsersRowStatus';
import { generateDropdownItems } from './utils';

import type { User } from 'API';

import './styles/SystemUsersRow.scss';

interface Props {
    user: User;
}

const SystemUsersRow = ({ user }: Props) => {
    const dispatch = useDispatch();
    const activeUser = useSelector(getActiveUser);
    const activeSidebar = useSelector(getActiveSidebar);
    const isActive = activeUser?.id === user.id && activeSidebar === ActiveSidebar.USERS;
    const { can: canEditUsers } = useCan({ type: 'users', action: 'edit' });
    const { can: canDeleteUsers } = useCan({ type: 'users', action: 'delete' });

    const selectUser = (event: Event) => {
        const element = event.target as HTMLInputElement;
        const elementTestId = element.getAttribute('data-testid');
        if (elementTestId && elementTestId.includes('user-settings-row-info')) {
            dispatch(setActiveSidebar(ActiveSidebar.USERS));
            dispatch(setActiveUser(user));
        }
    };

    const openUserDialog = () => {
        dispatch(setUserDialog({ user, showUserDialog: true }));
    };

    const openResetPasswordDialog = () => {
        dispatch(setActiveUser(user));
        dispatch(setSystemAdminOpenDialog(SystemAdminDialogTypes.RESET_PASSWORD_DIALOG));
    };

    const openDeleteUserDialog = () => {
        dispatch(setActiveUser(user));
        dispatch(setSystemAdminOpenDialog(SystemAdminDialogTypes.DELETE_USER_DIALOG));
    };

    const dropdownItems = generateDropdownItems({
        user,
        openUserDialog,
        openResetPasswordDialog,
        openDeleteUserDialog,
        canEditUsers,
        canDeleteUsers,
    });

    useEffect(() => {
        const element = document.getElementById(user.id);
        if (element) {
            element.addEventListener('click', selectUser);
            return () => {
                element.removeEventListener('click', selectUser);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, selectUser]);

    return (
        <tr
            id={user.id}
            className={classnames('SystemUsersRow', {
                active: isActive,
            })}
        >
            <td
                data-testid='user-settings-row-info-firstName'
                className='SystemUsersRow__column SystemUsersRow__column--breakLine'
            >
                {user.firstName}
            </td>
            <td
                data-testid='user-settings-row-info-lastName'
                className='SystemUsersRow__column SystemUsersRow__column--breakLine'
            >
                {user.lastName}
            </td>
            <td
                data-testid='user-settings-row-info-email'
                className='SystemUsersRow__column SystemUsersRow__column--breakLine'
            >
                {user.email}
            </td>
            <td
                data-testid='user-settings-row-info-phone'
                className='SystemUsersRow__column SystemUsersRow__column--breakLine'
            >
                {user.phone ? user.phone : <NoData className='text-14' />}
            </td>
            <td
                data-testid='user-settings-row-info-country'
                className='SystemUsersRow__column SystemUsersRow__column--breakLine'
            >
                {user.country ? user.country : <NoData className='text-14' />}
            </td>
            <td
                data-testid='user-settings-row-info-city'
                className='SystemUsersRow__column SystemUsersRow__column--breakLine'
            >
                {user.city ? user.city : <NoData className='text-14' />}
            </td>
            <td
                data-testid='user-settings-row-info-team'
                className='SystemUsersRow__column SystemUsersRow__column--breakLine'
            >
                {user.teams?.length ? (
                    user.teams
                        ?.map(team => team.name)
                        .join(', ')
                        .toString()
                ) : (
                    <NoData className='text-14' />
                )}
            </td>
            <td
                data-testid='user-settings-row-info-role'
                className='SystemUsersRow__column SystemUsersRow__column--breakLine'
            >
                {user.roles?.length ? (
                    user.roles
                        .map(role => role.name)
                        .join(', ')
                        .toString()
                ) : (
                    <NoData className='text-14' />
                )}
            </td>
            <td data-testid='user-settings-row-info-status' className='SystemUsersRow__column'>
                <SystemUsersRowStatus status={user.status} />
            </td>
            <td
                data-testid='user-settings-row-info-created'
                className='SystemUsersRow__column SystemUsersRow__column--breakLine'
            >
                <DateOrNoData value={user.createdAt} />
            </td>
            <td
                data-testid='user-settings-row-info-updated'
                className='SystemUsersRow__column SystemUsersRow__column--breakLine'
            >
                <DateOrNoData value={user.updatedAt} />
            </td>
            <td
                data-testid='user-settings-row-dropdown-icon'
                className='SystemUsersRow__dropdownColumn'
                onClick={e => e.stopPropagation()}
            >
                <ButtonDropdown
                    title={<span className='rioglyph rioglyph-option-vertical SystemUsersRow__dropdown' />}
                    items={dropdownItems}
                    disabled={dropdownItems.length === 0}
                    className='SystemUsersRow__dropdownButton'
                />
            </td>
        </tr>
    );
};

export default SystemUsersRow;
